import { Methods } from '../../api/methods.js';
import type { ParsedArgs } from '../args.js';
import { booleanFlag, flag, hasFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { pickDefined, withClient } from './common.js';

export async function mappingsCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'list';
  switch (action) {
    case 'list':
      return request(
        args,
        Methods.Mappings,
        hasFlag(args.flags, 'include-read-only') ? { includeReadOnly: true } : undefined,
      );
    case 'add': {
      const params = mappingParams(args);
      if (!params?.type || !params.match || !params.pattern) {
        throw new CliError('mappings add requires --type, --match, and --pattern', ExitCode.Usage);
      }
      return request(args, Methods.MappingsNew, params);
    }
    case 'update': {
      const id = Number(args.positionals[2]);
      if (!Number.isInteger(id) || id <= 0)
        throw new CliError('mappings update requires positive <id>', ExitCode.Usage);
      const params = mappingParams(args);
      if (!params) throw new CliError('mappings update requires a changed field', ExitCode.Usage);
      return request(args, Methods.MappingsUpdate, { id, ...params });
    }
    case 'delete': {
      const id = Number(args.positionals[2]);
      if (!Number.isInteger(id) || id <= 0)
        throw new CliError('mappings delete requires positive <id>', ExitCode.Usage);
      return request(args, Methods.MappingsDelete, { id });
    }
    case 'reload':
      return request(args, Methods.MappingsReload);
    default:
      throw new CliError(`Unknown mappings action "${action}"`, ExitCode.Usage);
  }
}

function mappingParams(args: ParsedArgs): Record<string, unknown> | undefined {
  return pickDefined({
    label: flag(args.flags, 'label'),
    type: flag(args.flags, 'type'),
    match: flag(args.flags, 'match'),
    pattern: flag(args.flags, 'pattern'),
    override: flag(args.flags, 'override'),
    enabled: booleanFlag(args.flags, 'enabled'),
  });
}

async function request(args: ParsedArgs, method: string, params?: unknown): Promise<CommandResult> {
  const data = await withClient(args.options, (client) => client.request(method, params));
  return { data };
}
